import React, { useEffect, useState } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { withStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import ScheduleIcon from '@material-ui/icons/Schedule';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';
import PhoneOutlinedIcon from '@material-ui/icons/PhoneOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LocalHospitalOutlinedIcon from '@material-ui/icons/LocalHospitalOutlined';
import BusinessOutlinedIcon from '@material-ui/icons/BusinessOutlined';
import CalendarTodayOutlinedIcon from '@material-ui/icons/CalendarTodayOutlined';
import DeleteForeverOutlinedIcon from '@material-ui/icons/DeleteForeverOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';

import { error } from 'variables/colors';
import { FlexCenterVertical } from 'styles/flexbox';
import { border } from 'variables/general';
import { showSubLocation } from 'api/sub-location';
import { deleteAppointment } from 'api/appointment';
import { useSnackBar } from 'context/SnackBarContext';
import InfoSection from './InfoSection';
import { subLocationNameGenerator } from 'helpers/site-name-generator';
import { showOrganization } from 'api/organization';

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: '1.5em',
  },
  deleteButton: {
    position: 'absolute',
    right: '2em',
    top: theme.spacing(1),
    color: error,
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
});

const Component = styled.div``;

const ParticipantName = styled.h2`
  margin-bottom: 1.5em;
`;

const DialogTitleContainer = styled.div`
  height: 4em;
`;

const DateTime = styled.section`
  margin-bottom: 1.5em;
  padding-bottom: 1.5em;
  font-size: 0.75em;
  border-bottom: ${border};
`;

const Date = styled.h4`
  margin-bottom: 0.75em;
`;

const TimeContainer = styled(FlexCenterVertical)``;

const Time = styled.div`
  margin-left: 1em;
`;

const DialogTitle = withStyles(styles)((props) => {
  const {
    appointmentUuid,
    classes,
    handleClose,
    onClose,
    setMessage,
    setSeverity,
    setOpenSnackBar,
    fetchAppointments,
    ...other
  } = props || {};

  const handleDelete = async () => {
    try {
      await deleteAppointment(appointmentUuid);
      setOpenSnackBar(true);
      setSeverity('success');
      setMessage('Appointment deleted.');
    } catch {
      setOpenSnackBar(true);
      setSeverity('error');
      setMessage('Something went wrong. Failed to delete participant.');
    }

    try {
      await fetchAppointments();
    } catch {
      setOpenSnackBar(true);
      setSeverity('error');
      setMessage('Something went wrong. Failed to fetch updated appointments');
    }

    handleClose();
  };

  return (
    <DialogTitleContainer>
      <MuiDialogTitle disableTypography className={classes.root} {...other}>
        <IconButton
          aria-label="delete"
          className={classes.deleteButton}
          onClick={handleDelete}
        >
          <DeleteForeverOutlinedIcon />
        </IconButton>

        {onClose ? (
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </MuiDialogTitle>
    </DialogTitleContainer>
  );
});

const DialogContent = withStyles(() => ({
  root: {
    padding: '1.5em',
  },
}))(MuiDialogContent);

export default function CustomizedDialogs({
  appointment,
  modalOpen,
  setModalOpen,
  fetchAppointments,
}) {
  const [subLocationName, setSubLocationName] = useState('');
  const [organization, setOrganization] = useState({});

  const { start, end, resources } = appointment || {};
  const { uuid, fullName, participant, clinic, service } = resources || {};
  const { email, phone, customFieldName, customFieldValue } = participant || {};
  const { clinicId, subLocationId, organizationId } = clinic || {};
  const { serviceName, defaultDuration } = service || {};
  const { name: orgName } = organization;

  const { setMessage, setSeverity, setOpenSnackBar } = useSnackBar();

  const handleClose = () => {
    setModalOpen(false);
  };

  useEffect(async () => {
    if (subLocationId) {
      const { data } = await showSubLocation(subLocationId);

      setSubLocationName(subLocationNameGenerator(data.data));
    }
  }, [subLocationId]);

  useEffect(async () => {
    if (organizationId) {
      const { data } = await showOrganization(organizationId);

      setOrganization(data.data);
    }
  }, [organizationId]);

  const info = [
    {
      sectionHeading: 'Participant',
      data: [
        {
          icon: <EmailOutlinedIcon fontSize="small" />,
          description: email,
        },
        ...(phone
          ? [
              {
                icon: <PhoneOutlinedIcon fontSize="small" />,
                description: phone,
              },
            ]
          : []),
        ...(customFieldValue
          ? [
              {
                icon: <BadgeOutlinedIcon fontSize="small" />,
                description: `${customFieldName} - ${customFieldValue}`,
              },
            ]
          : []),
      ],
    },
    {
      sectionHeading: 'Clinic',
      data: [
        {
          icon: <LocalHospitalOutlinedIcon fontSize="small" />,
          description: `ID - ${clinicId}`,
        },
        {
          icon: <BusinessOutlinedIcon fontSize="small" />,
          description: orgName,
        },
        {
          icon: <LocationOnOutlinedIcon fontSize="small" />,
          description: subLocationName,
        },
        {
          icon: <CalendarTodayOutlinedIcon fontSize="small" />,
          description: moment(start).format('MMM D, YYYY'),
        },
      ],
    },
    {
      sectionHeading: 'Service',
      data: [
        {
          icon: <EmailOutlinedIcon fontSize="small" />,
          description: serviceName,
        },
        {
          icon: <ScheduleIcon fontSize="small" />,
          description: `${defaultDuration} Mins`,
        },
      ],
    },
  ];

  return (
    <Component>
      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={modalOpen}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          id="customized-dialog-title"
          onClose={handleClose}
          appointmentUuid={uuid}
          handleClose={handleClose}
          setMessage={setMessage}
          setSeverity={setSeverity}
          setOpenSnackBar={setOpenSnackBar}
          fetchAppointments={fetchAppointments}
        >
          Appointment
        </DialogTitle>
        <DialogContent appointment={appointment} dividers>
          <ParticipantName>{fullName}</ParticipantName>

          <DateTime>
            <Date>{moment(start).format('dddd, MMMM D, YYYY')}</Date>
            <TimeContainer>
              <ScheduleIcon fontSize="small" />
              <Time>
                {moment(start).format('h:mm')} - {moment(end).format('h:mma')}
              </Time>
            </TimeContainer>
          </DateTime>

          {info.map(({ sectionHeading, data }) => (
            <InfoSection heading={sectionHeading} data={data} />
          ))}
        </DialogContent>
      </Dialog>
    </Component>
  );
}
